import { t as translate } from 'i18n';
import { tokens } from 'theme';
import { kpiDelta, trendCard } from './trendCard';
import { buildEChartsOption } from './echartsOptions';
import { SIDE, buildTrendOption } from './trendCardOption';
import { formatAmount, formatShare, unitOf } from './units';
import { buildPieData, kpiValues, normalizeRows } from './widgetData';

/**
 * What a chart becomes on a slide. It is rebuilt rather than copied from the
 * screen: always the light mode of the active theme, on an opaque card, with
 * the header that the screen draws in the DOM. The result is plain data, so
 * both file formats and the tests share one layout.
 */
const CARD_WIDTHS = { full: 1200, half: 800, quarter: 560 };
const CARD_RADIUS = 16;
const PADDING = 40;
const GAP = 8;
const LINE = 1.25;
const CHART_HEIGHTS = { full: 460, half: 400, quarter: 260 };
// Slides are read from across a room: everything is larger than on a screen.
const SLIDE_TYPE = { title: 24, titleWeight: 600, value: 48, valueWeight: 600, label: 16, axis: 15 };
const CARD_KINDS = new Set(['bar_card', 'line_card']);
const CIRCULAR = new Set(['pie', 'doughnut']);
const RING = ['50%', '70%'];
const POINT_KINDS = new Set(['scatter', 'bubble']);
// Rows the screen sizes one by one, so that every one of them keeps its name.
const ROW_HEIGHTS = { ranked_bar: { row: 24, chrome: 56 }, bullet: { row: 32, chrome: 56 }, heatmap: { row: 20, chrome: 80 } };
const MAX_CHART_HEIGHT = 4000;
const LEGEND_ROW = 30;
const Y_NAME_GAP = 84;
const UNMATCHED_SHOWN = 10;

/**
 * On a screen these labels live in the DOM, so the canvas must draw them here,
 * in the same words: the same slices and formats as the list beside the chart.
 */
function withOwnLabels(option, chart, columns, rows, theme, width) {
  if (chart.kind !== 'funnel' && !CIRCULAR.has(chart.kind)) return option;
  const pie = buildPieData(chart, columns, rows, theme.chart.series);
  // By index: two slices can share a name, and each keeps its own number.
  const amount = ({ name, dataIndex }) => `${name}: ${formatAmount(pie.slices[dataIndex]?.value, pie.unit)}`;
  const share = ({ name, dataIndex }) => `${name}\n${formatShare(pie.slices[dataIndex]?.share)}`;
  const [series, ...rest] = option.series;
  const shape = chart.kind === 'funnel'
    ? { left: '4%', width: '56%', label: { ...series.label, show: true, position: 'right', width: Math.floor(width * 0.36), overflow: 'break', formatter: amount } }
    : { radius: chart.kind === 'doughnut' ? RING : '66%', label: { ...series.label, show: true, lineHeight: theme.type.axis * LINE, overflow: 'break', formatter: share } };
  return { ...option, series: [{ ...series, ...shape, labelLine: { show: true } }, ...rest] };
}

function slideTheme(themeId, fontFamily, height) {
  const light = tokens('light', themeId);
  // The total sits in the ring's hole, so it grows with the hole and not the card.
  const type = { ...SLIDE_TYPE, valueCompact: Math.round(height * 0.07) };
  return { ...light, chart: { ...light.chart, type }, type, fontFamily };
}

function trendPlan(widget, columns, rows, meta, theme, width, height) {
  const card = trendCard(widget.chart, columns, rows, meta);
  if (card.fallback) return null;
  const view = { selected: card.selected, width: width - 2 * (PADDING - SIDE) };
  const option = buildTrendOption({ card, view, kind: widget.chart.kind, tone: widget.chart.tone, theme, height, compact: false });
  return { option, header: card, note: card.partial ? translate('assistant.trendCardPartial') : '' };
}

/** The pager of a scroll legend means nothing on paper, so every name is drawn. */
function fullLegend(option, theme, room) {
  const { legend } = option;
  if (!legend || Array.isArray(legend) || legend.show === false || Array.isArray(option.grid)) return { option, extra: 0 };
  const size = theme.type.axis;
  let rows = 1;
  let used = 0;
  for (const { name = '' } of option.series) {
    const entry = String(name).length * size * 0.62 + 36;
    if (used && used + entry > room) { rows += 1; used = 0; }
    used += entry;
  }
  const extra = (rows - 1) * LEGEND_ROW;
  return { option: { ...option, legend: { ...legend, type: 'plain' }, grid: { ...option.grid, top: (option.grid?.top || 0) + extra } }, extra };
}

/** Rows the map could not place stay on the slide, as they do under the map on screen. */
function unmatchedNote(unmatched, chart, columns) {
  if (!unmatched?.length) return '';
  const column = columns.find((item) => item.key === chart.y?.[0]);
  const listed = unmatched.slice(0, UNMATCHED_SHOWN).map((row) => `${row[chart.x]}: ${formatAmount(row[chart.y?.[0]], unitOf(column))}`);
  const more = unmatched.length > UNMATCHED_SHOWN ? ' …' : '';
  return translate('assistant.exportUnmatched', { value1: `${listed.join(', ')}${more}`, interpolation: { escapeValue: false } });
}

function chartPlan(widget, columns, rows, meta, theme, width, height) {
  const { chart } = widget;
  const room = width - 2 * PADDING;
  const result = buildEChartsOption(chart, columns, rows, theme.chart.series, theme, meta);
  if (result.error || !result.option) return null;
  if (result.selection) result.selection.view.width = room;
  const tall = ROW_HEIGHTS[chart.kind];
  const count = result.option.yAxis?.[0]?.data?.length || 0;
  const body = tall ? Math.min(MAX_CHART_HEIGHT, Math.max(height, count * tall.row + tall.chrome)) : height;
  const { option, extra } = fullLegend(withOwnLabels(result.option, chart, columns, rows, theme, room), theme, room);
  // The slide's larger ticks need more room than the axis name keeps on screen.
  const placed = POINT_KINDS.has(chart.kind) ? { ...option, yAxis: option.yAxis.map((axis) => (axis.name ? { ...axis, nameGap: Y_NAME_GAP } : axis)) } : option;
  const header = chart.value ? trendCard(chart, columns, rows, meta) : null;
  const note = { bars: 'assistant.trendCardPartial', points: 'assistant.trendCardPartialPoint' }[result.partial];
  return {
    option: placed,
    patch: result.selection?.patch(),
    header: header && !header.fallback ? header : null,
    note: note ? translate(note) : unmatchedNote(result.unmatched, chart, columns),
    chartHeight: body + extra,
  };
}

function kpiPlan(widget, columns, rows, meta) {
  const tiles = kpiValues(widget.chart, columns, rows);
  if (tiles.fallback || !tiles.length) return null;
  const [first, ...rest] = tiles;
  const name = tiles.length > 1 || !widget.title ? first.title : '';
  return { option: null, header: { name, value: first.value, delta: kpiDelta(widget.chart, columns, rows, meta), lines: rest.map((tile) => `${tile.title}: ${tile.value}`) } };
}

/**
 * Nothing on a file ever moves: an animated or progressive draw would be read
 * at its first frame, and a chart would come out with its marks missing.
 */
function still(option) {
  const series = [].concat(option.series || []).map((item) => {
    const marks = ['markPoint', 'markLine', 'markArea'].filter((key) => item[key]);
    return { ...item, animation: false, progressive: 0, ...Object.fromEntries(marks.map((key) => [key, { ...item[key], animation: false }])) };
  });
  return { ...option, animation: false, series };
}

/** Everything both formats draw, or null when the widget has nothing to export. */
export function exportPlan({ widget, columns, rows: inputRows, meta = {}, themeId, fontFamily }) {
  const { chart } = widget;
  if (!chart) return null;
  const rows = normalizeRows(inputRows, columns);
  if (!rows.length) return null;
  const width = CARD_WIDTHS[widget.width] || CARD_WIDTHS.full;
  const height = CHART_HEIGHTS[widget.width] || CHART_HEIGHTS.full;
  const theme = slideTheme(themeId, fontFamily, height);
  const body = chart.kind === 'kpi' ? kpiPlan(widget, columns, rows, meta)
    : CARD_KINDS.has(chart.kind) ? trendPlan(widget, columns, rows, meta, theme, width, height)
      : chartPlan(widget, columns, rows, meta, theme, width, height);
  if (!body) return null;
  return { note: '', chartHeight: body.option ? height : 0, ...body, option: body.option && still(body.option), title: widget.title || '', theme, width };
}

/** Draws a plan on an engine the way the screen does: the selection goes on top. */
export function paint(engine, plan) {
  engine.setOption(plan.option, { silent: true });
  if (plan.patch) engine.setOption(plan.patch, { silent: true });
}

const fitted = (text, room, measure) => {
  if (measure(text) <= room) return text;
  let end = text.length;
  while (end > 0 && measure(`${text.slice(0, end)}…`) > room) end -= 1;
  return `${text.slice(0, end).trimEnd()}…`;
};

const wrapped = (text, room, measure) => {
  const lines = [];
  for (const word of text.split(' ')) {
    const line = lines.length ? `${lines[lines.length - 1]} ${word}` : word;
    if (lines.length && measure(line) <= room) lines[lines.length - 1] = line;
    else lines.push(word);
  }
  return lines;
};

/**
 * The card as positioned shapes. `measure(text, size, weight)` is the renderer's
 * own, so a long title is cut where that renderer's font actually ends.
 */
export function layout(plan, measure) {
  const { theme, width, title, header, note } = plan;
  const { type } = theme;
  const room = width - 2 * PADDING;
  const shapes = [];
  let y = PADDING;
  const text = (content, size, weight, fill, x = PADDING) => {
    if (!content) return;
    const line = fitted(content, room - (x - PADDING), (value) => measure(value, size, weight));
    shapes.push({ kind: 'text', text: line, x, y: y + size, size, weight, fill });
    y += size * LINE + GAP;
  };
  text(title, type.title, type.titleWeight, theme.ink);
  if (header) {
    y += GAP;
    text(header.name, type.label, 400, theme.inkMuted);
    text(header.value, type.value, type.valueWeight, theme.ink);
    const { delta } = header;
    if (delta) {
      const size = type.label;
      const pad = 10;
      const badge = measure(delta.text, size, 600) + 2 * pad;
      const height = size * LINE + pad;
      shapes.push({ kind: 'box', x: PADDING, y, width: badge, height, radius: 8, fill: delta.good ? theme.positiveBg : theme.negativeBg });
      shapes.push({ kind: 'text', text: delta.text, x: PADDING + pad, y: y + height / 2 + size / 2.8, size, weight: 600, fill: delta.good ? theme.positive : theme.negative });
      shapes.push({ kind: 'text', text: fitted(delta.label, room - badge - GAP, (value) => measure(value, size, 400)), x: PADDING + badge + GAP, y: y + height / 2 + size / 2.8, size, weight: 400, fill: theme.inkMuted });
      y += height + GAP;
    }
    text(header.caption, type.label, 400, theme.inkMuted);
    (header.lines || []).forEach((line) => text(line, type.label, 400, theme.inkMuted));
  }
  y += GAP;
  const chartTop = y;
  y += plan.chartHeight;
  if (note) {
    y += GAP;
    wrapped(note, room, (value) => measure(value, type.axis, 400)).forEach((line) => text(line, type.axis, 400, theme.inkMuted));
  }
  return { shapes, chartTop, chartLeft: PADDING, chartWidth: room, height: Math.ceil(y + PADDING - (note ? GAP : 0)), radius: CARD_RADIUS };
}

// XML 1.0 forbids these even when escaped, and tenant data can hold them.
const NOT_XML = /[^\t\n\r -퟿-�\u{10000}-\u{10FFFF}]/gu;
const escapeXml = (value) => String(value).replace(NOT_XML, '').replace(/[&<>"']/g, (mark) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[mark]));

/**
 * The engine writes attributes unescaped, and a chart's fonts and colours come
 * from the model, so its own SVG string could carry a live script into a file
 * someone opens in a browser. The file is written here from the engine's tree
 * instead: only drawing elements, only their attributes, everything escaped.
 */
const SVG_TAGS = new Set(['svg', 'g', 'defs', 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'text', 'tspan', 'image', 'clipPath', 'linearGradient', 'radialGradient', 'stop', 'pattern', 'filter', 'feDropShadow']);
const SVG_ATTRIBUTE = /^(?!on|ecmeta_)[a-z][\w:.-]*$/i;
const REMOTE = /url\((?!#)|javascript:|\\|@import/i;
const LOCAL_LINK = /^(#|data:image\/(png|jpeg|gif|webp)[;,])/i;

function safeStyle(style) {
  const fonts = String(style).split(';').filter((part) => /^\s*font(-[a-z]+)?\s*:/i.test(part) && !REMOTE.test(part));
  return fonts.join(';');
}

function safeAttribute(name, value) {
  if (value === false || value === true || value == null || !SVG_ATTRIBUTE.test(name)) return null;
  if (name === 'style') return safeStyle(value) || null;
  if (/href$/i.test(name)) return LOCAL_LINK.test(value) ? value : null;
  return REMOTE.test(value) ? null : value;
}

export function safeSvg({ tag, attrs, children, text }, placement = {}) {
  if (!SVG_TAGS.has(tag)) return '';
  const written = Object.entries({ ...attrs, ...placement }).flatMap(([name, value]) => {
    const safe = safeAttribute(name, value);
    return safe === null ? [] : [`${name}="${escapeXml(safe)}"`];
  });
  return `<${[tag, ...written].join(' ')}>${escapeXml(text ?? '')}${(children || []).map((child) => safeSvg(child)).join('')}</${tag}>`;
}

/** The chart as the engine's element tree, drawn with nothing that moves. */
export const svgTree = (engine) => engine.getZr().painter.renderToVNode({ animation: false, emphasis: false, willUpdate: false, compress: true, useViewBox: true });

function svgShape(shape, font) {
  if (shape.kind === 'box') return `<rect x="${shape.x}" y="${shape.y}" width="${shape.width}" height="${shape.height}" rx="${shape.radius}" fill="${escapeXml(shape.fill)}"/>`;
  return `<text x="${shape.x}" y="${shape.y}" font-family="${escapeXml(font)}" font-size="${shape.size}" font-weight="${shape.weight}" fill="${escapeXml(shape.fill)}">${escapeXml(shape.text)}</text>`;
}

/** The whole card as one file; the chart arrives as the engine's element tree. */
export function cardSvg(plan, placed, chart) {
  const { width, theme } = plan;
  const { height, radius, shapes, chartLeft, chartTop } = placed;
  const inner = chart ? safeSvg(chart, { x: chartLeft, y: chartTop }) : '';
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
    `<rect width="${width}" height="${height}" rx="${radius}" fill="${escapeXml(theme.surface)}"/>`,
    ...shapes.map((shape) => svgShape(shape, theme.fontFamily)),
    inner,
    '</svg>',
  ].join('');
}

/** By hand: `roundRect` on a canvas is missing from older Safari. */
function roundedPath(context, x, y, width, height, radius) {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.arcTo(x + width, y, x + width, y + height, radius);
  context.arcTo(x + width, y + height, x, y + height, radius);
  context.arcTo(x, y + height, x, y, radius);
  context.arcTo(x, y, x + width, y, radius);
  context.closePath();
}

/** The same shapes on a canvas, for a raster the slide software can't restyle. */
export function drawShapes(context, plan, placed) {
  const { theme, width } = plan;
  const { height, radius, shapes } = placed;
  context.fillStyle = theme.surface;
  roundedPath(context, 0, 0, width, height, radius);
  context.fill();
  for (const shape of shapes) {
    context.fillStyle = shape.fill;
    if (shape.kind === 'box') {
      roundedPath(context, shape.x, shape.y, shape.width, shape.height, shape.radius);
      context.fill();
    } else {
      context.font = `${shape.weight} ${shape.size}px ${theme.fontFamily}`;
      context.fillText(shape.text, shape.x, shape.y);
    }
  }
}
